import _ from "lodash";
import Backbone from "backbone";
import HorizontalScrollerBehavior from "scripts/hoc/horizontalScrollerBehavior";
import BaseView from "scripts/views/baseView";
import {
  facetParamOrder,
  facetNameForFacetParam,
  facetLabelForFacetParam,
  termLabelsForTermCodes,
} from "scripts/views/search/facetMapper";
import FacetedSearchTermModalView from "scripts/views/search/facetedSearchTermModalView";
import FacetedSearchCheckboxMultiSelectModalView from "scripts/views/search/facetedSearchCheckboxMultiSelectModalView";
import FacetedSearchComboBoxMultiSelectModalView from "scripts/views/search/facetedSearchComboBoxMultiSelectModalView";
import FacetedSearchPublicationYearModalView from "scripts/views/search/facetedSearchPublicationYearModalView";
import FacetedSearchQueryPillView from "scripts/views/search/facetedSearchQueryPillView";

import templateFacetedSearchQueryMenu from "templates/search/facetedSearchQueryMenu.hbs";

const chan = Backbone.Radio.channel;

class FacetedSearchQueryMenuView extends HorizontalScrollerBehavior(BaseView) {
  constructor(options) {
    super(options);

    this.pillViews = new Map();
  }

  get template() {
    return templateFacetedSearchQueryMenu;
  }

  get id() {
    return "bb-faceted-search-query-menu";
  }

  get className() {
    return "faceted-search-query-menu";
  }

  get events() {
    return {
      "click .bb-horizontal-scroller-right-button": "onClickScrollRight",
      "click .bb-horizontal-scroller-left-button": "onClickScrollLeft",
    };
  }

  getSearchTerm() {
    return this.model.get("queryParams")["g"];
  }

  getTermsMap() {
    const queryParams = this.model.get("queryParams");
    const termsMap = new Map();

    Object.keys(queryParams).forEach(facetParam => {
      //key: facetParam, value: Array of termCodes
      if (facetParam !== "g" && facetParamOrder.includes(facetParam)) {
        if (facetParam === "_pd") {
          termsMap.set(facetParam, queryParams[facetParam].split(" OR "));
        } else {
          termsMap.set(
            facetParam,
            queryParams[facetParam]
              .split('" OR "')
              .map(term => term.replace(/"/g, "")),
          );
        }
      }
    });

    return termsMap;
  }

  getTermsParams(termsMap) {
    const termsParams = {};

    Array.from(termsMap.keys()).forEach(queryParam => {
      termsParams[queryParam] = termsMap
        .get(queryParam)
        .map(term => {
          if (queryParam === "_pd") {
            return term;
          } else {
            return `"${term}"`;
          }
        })
        .join(" OR ");
    });

    return termsParams;
  }

  getQueryParams(searchTerm, termsMap) {
    const queryParams = {};

    if (searchTerm) {
      queryParams["g"] = searchTerm;
    } else {
      queryParams["g"] = "*";
    }

    return _.merge(queryParams, this.getTermsParams(termsMap));
  }

  update() {
    this.render();
  }

  render() {
    this.$el.html(this.template());

    const searchTerm = this.getSearchTerm();

    if (searchTerm && searchTerm !== "*") {
      this.addSearchTermPill(searchTerm);
    }

    const termsMap = this.getTermsMap();

    Array.from(termsMap.keys()).forEach(facetParam =>
      this.addFacetPill(facetParam, termsMap.get(facetParam)),
    );

    this.initializeScrollerBehavior();
  }

  getFacetPillValue(facetParam, termCodes) {
    const termLabels = termLabelsForTermCodes(
      this.collection.facets,
      facetParam,
      termCodes,
    );
    return termLabels.length > 1 ? `(${termLabels.length})` : termLabels[0];
  }

  createSearchTermPill(searchTerm) {
    const pillView = new FacetedSearchQueryPillView({
      param: "g",
      label: "Search",
      value: searchTerm,
      hasValues: true,
    });

    pillView.on("close", () => {
      this.removeSearchTerm();
    });

    pillView.on("click", () => {
      this.showSearchTermModal();
    });

    return pillView;
  }

  removeSearchTerm() {
    this.removeSearchTermPill();
    this.model.set(
      "queryParams",
      this.getQueryParams(null, this.getTermsMap()),
    );
  }

  removeSearchTermPill() {
    const pillView = this.pillViews.get("g");
    this.pillViews.delete("g");
    this.closeSubView(pillView);
  }

  addSearchTermPill(searchTerm) {
    const pillView = this.createSearchTermPill(searchTerm);

    this.pillViews.set("g", pillView);

    this.addSubView(pillView)
      .appendTo(this.$el.find(".bb-horizontal-scroller-content"))
      .render();
  }

  hasFacetValues(facetParam) {
    const facet = this.collection.facets[facetNameForFacetParam(facetParam)];
    console.log("facet", facet);
    return facet && facet.facetValues.length > 0;
  }

  createFacetPill(facetParam, termCodes) {
    const facetLabel = facetLabelForFacetParam(facetParam);

    console.log(
      "has values facetParam: %O, hasValues: %O",
      facetParam,
      this.hasFacetValues(facetParam),
    );

    const pillView = new FacetedSearchQueryPillView({
      param: facetParam,
      label: facetLabel,
      value: this.getFacetPillValue(facetParam, termCodes),
      hasValues: this.hasFacetValues(facetParam),
    });

    pillView.on("close", facetParam => {
      this.removeFacetParam(facetParam);
    });

    pillView.on("click", facetParam => this.showFacetModal(facetParam));

    return pillView;
  }

  removeFacetParam(facetParam) {
    this.removeFacetPill(facetParam);

    const termsMap = this.getTermsMap();
    termsMap.delete(facetParam);

    this.model.set(
      "queryParams",
      this.getQueryParams(this.getSearchTerm(), termsMap),
    );

    this.updateScrollButtonVisibility();
  }

  removeFacetPill(facetParam) {
    const pillView = this.pillViews.get(facetParam);
    this.pillViews.delete(facetParam);
    this.closeSubView(pillView);
  }

  addFacetPill(facetParam, termCodes) {
    const pillView = this.createFacetPill(facetParam, termCodes);

    this.pillViews.set(facetParam, pillView);

    this.addSubView(pillView)
      .appendTo(this.$el.find(".bb-horizontal-scroller-content"))
      .render();

    //TODO: allow this here
    //this.updateScrollButtonVisibility();
  }

  showSearchTermModal() {
    if (!this.model.get("loading")) {
      const modal = new FacetedSearchTermModalView({
        searchTerm: this.getSearchTerm(),
      });

      modal.on("apply", searchTerm => this.onSearchModalApply(searchTerm));

      modal.on("close", () => {
        $(".faceted-search-query-pill-link").focus();
      });

      chan("display").request("showModal", modal);
    } else {
      console.log("Currently loading, ignoring search term modal click");
    }
  }

  onSearchModalApply(searchTerm) {
    const pillView = this.pillViews.get("g");

    if (searchTerm) {
      if (pillView) {
        pillView.value = searchTerm;
        pillView.render();
      } else {
        this.addSearchTermPill(searchTerm);
      }

      this.model.set(
        "queryParams",
        this.getQueryParams(searchTerm, this.getTermsMap()),
      );
    } else {
      this.removeSearchTerm();
    }
  }

  showFacetModal(facetParam) {
    if (!this.model.get("loading")) {
      const facetLabel = facetLabelForFacetParam(facetParam);
      const facetName = facetNameForFacetParam(facetParam);

      const termsMap = this.getTermsMap();
      const facetValues =
        (this.collection.facets[facetName] || {}).facetValues || [];
      const facetModal = this.createFacetModal(
        facetParam,
        facetLabel,
        facetValues,
        termsMap.get(facetParam) || [],
      );

      facetModal.on("apply", (facetParam, termCodes) =>
        this.onFacetModalApply(facetParam, termCodes),
      );

      facetModal.on("close", () => {
        $(".faceted-search-query-pill-link").focus();
      });

      chan("display").request("showModal", facetModal);
    } else {
      console.log("Currently loading, ignoring facet modal click");
    }
  }

  createFacetModal(facetParam, facetLabel, facetValues, chosenTermCodes) {
    const model = new Backbone.Model({
      facetParam,
      facetLabel,
      facetValues,
      chosenTermCodes,
    });

    if (facetParam === "_pd") {
      return new FacetedSearchPublicationYearModalView({ model });
      //} else if ((facetValues.length > 10) && chan('display').request('isLargerThanXs')) {
    } else if (facetValues.length > 10) {
      return new FacetedSearchComboBoxMultiSelectModalView({ model });
    } else {
      return new FacetedSearchCheckboxMultiSelectModalView({ model });
    }
  }

  onFacetModalApply(facetParam, termCodes) {
    if (termCodes && termCodes.length) {
      this.includeFacetTerms(facetParam, termCodes);
    } else {
      this.removeFacetParam(facetParam);
    }
  }

  includeFacetTerms(facetParam, termCodes) {
    const termsMap = this.getTermsMap();
    const pillView = this.pillViews.get(facetParam);

    if (pillView) {
      pillView.value = this.getFacetPillValue(facetParam, termCodes);
      pillView.render();
    } else {
      this.addFacetPill(facetParam, termCodes);
      this.updateScrollButtonVisibility();
    }

    termsMap.set(facetParam, termCodes);

    this.model.set(
      "queryParams",
      this.getQueryParams(this.getSearchTerm(), termsMap),
    );
  }
}

export default FacetedSearchQueryMenuView;
